// src/pages/purchasing/EditPurchaseOrderWithAI.tsx
import * as Yup from 'yup';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs } from 'src/components/organisms/ContentCard';
import { Formik, FormikHelpers } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import LocalShippingTwoToneIcon from '@mui/icons-material/LocalShippingTwoTone';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import InsertDriveFileTwoToneIcon from '@mui/icons-material/InsertDriveFileTwoTone';
import PictureAsPdfTwoToneIcon from '@mui/icons-material/PictureAsPdfTwoTone';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import useSubmit from 'src/hooks/forms/useSubmit';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { usePurchaseOrderForm, useSupplierForm } from 'src/hooks/forms/useForms';
import { usePurchaseOrderOptions } from 'src/hooks/forms/useOptions';
import { Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useSelectProductsOnPurchaseOrder } from 'src/hooks/table/configs/useSelectProductsOnPurchaseOrder';
import SelectTableDialog from 'src/components/organisms/tables/SelectTableDialog';
import { AxiosRequestConfig } from 'axios';
import { createOrUpdatePurchaseOrder, createPurchaseOrderRowsById, deletePurchaseOrderRowById, updatePurchaseOrderRowsById } from 'src/utils/apiRequests';
import { buildSubmitRequest } from 'src/utils/submit/buildSubmitRequest';
import FormHelper from 'src/utils/formHelper';
import AutoFixHighTwoToneIcon from '@mui/icons-material/AutoFixHighTwoTone';
import { Grid } from '@mui/material';
import { createPurchaseOrderPolicy, STATUS_ID } from './PurchaseOrderRules';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';
//rerun icon
import { ReplayTwoTone as ReplayTwoToneIcon } from '@mui/icons-material';
//upload icon
import { UploadFileOutlined } from '@mui/icons-material';
// --- Supplier-dependent validation helper ---
const supplierStepRequired = () =>
    Yup.mixed().test(
        'supplier-step-required',
        '',
        function (value) {
            const { Supplier } = this.parent as any;

            if (value !== null && value !== undefined && value !== '') {
                return true;
            }

            if (!Supplier) {
                return this.createError({
                    message: '1. Select supplier, then click the wand button.',
                });
            }

            return this.createError({
                message: '2. Click the wand button to autofill.',
            });
        }
    );

// --- Validation schema ---
const scheme = Yup.object().shape({
    Supplier: Yup.mixed().required('1. Select supplier.'),

    Recipient: Yup.mixed().required('Recipient is required.'),

    PaymentMethodId: supplierStepRequired(),
    PaymentTermId: supplierStepRequired(),
    ShippingMethod: supplierStepRequired(),
    DefaultCurrency_Currency: supplierStepRequired(),
});


type PurchaseOrderInvoiceCharge = {
    ChargeType: string;
    Sum: number | string;
    Currency: string;
};

type PurchaseOrderInvoiceExtraction = {
    FileName?: string;
    Charges: PurchaseOrderInvoiceCharge[];
    RawJson?: any;
};

type PurchaseOrderResource = {
    id: string;
    name: string;
    type: string;
    file?: File;
    source?: any;
    invoiceCharges?: PurchaseOrderInvoiceCharge[];
    invoiceRawJson?: any;
    invoiceAiStatus?: 'pending' | 'done' | 'error';
    invoiceAiError?: string;
};

const purchaseOrderInvoiceChargeTypes = ['Total', 'Goods', 'Packaging', 'Handling', 'Shipping', 'Customs'];

const normalizePurchaseOrderInvoiceChargeType = (chargeType: any) => {
    const normalizedChargeType = String(chargeType ?? '').trim().toLowerCase();
    return purchaseOrderInvoiceChargeTypes.find(type => type.toLowerCase() === normalizedChargeType) ?? null;
};

const normalizePurchaseOrderInvoiceSum = (sum: any): number | string | null => {
    if (typeof sum === 'number' && Number.isFinite(sum)) return sum;

    if (typeof sum === 'string') {
        const trimmedSum = sum.trim();
        if (!trimmedSum) return null;

        const normalizedNumber = Number(trimmedSum.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(normalizedNumber) ? normalizedNumber : trimmedSum;
    }

    return null;
};

const normalizePurchaseOrderInvoiceCharges = (charges: any): PurchaseOrderInvoiceCharge[] => {
    const rawCharges = Array.isArray(charges) ? charges : [];
    return rawCharges
        .map((charge: any) => {
            const ChargeType = normalizePurchaseOrderInvoiceChargeType(charge?.ChargeType ?? charge?.Type ?? charge?.type);
            const Sum = normalizePurchaseOrderInvoiceSum(charge?.Sum ?? charge?.Amount ?? charge?.Value ?? charge?.sum);
            const Currency = String(charge?.Currency ?? charge?.currency ?? '').trim().toUpperCase();

            if (!ChargeType || Sum === null || !Currency) return null;

            return { ChargeType, Sum, Currency };
        })
        .filter(Boolean) as PurchaseOrderInvoiceCharge[];
};

const getExtensionFromFileName = (fileName?: string) => {
    const extension = fileName?.split('.').pop();
    return extension && extension !== fileName ? extension.toLowerCase() : '';
};

const getResourceTypeLabel = (resource: PurchaseOrderResource) => {
    const rawType = String(resource?.type ?? '').toLowerCase();
    const extension = getExtensionFromFileName(resource?.name);

    if (rawType.includes('pdf') || rawType === 'pdf' || extension === 'pdf') return 'PDF';
    if (rawType.includes('word') || rawType === 'docx' || extension === 'docx') return 'DOCX';
    if (rawType === 'doc' || extension === 'doc') return 'DOC';
    if (rawType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(rawType) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return (extension === 'jpeg' || rawType === 'jpeg' ? 'JPG' : extension || rawType.replace('image/', '') || 'Image').toUpperCase();
    if (extension) return extension.toUpperCase();
    return rawType || 'Unknown';
};

const getResourceIcon = (resource: PurchaseOrderResource) => {
    const fileType = getResourceTypeLabel(resource).toLowerCase();

    if (fileType === 'pdf') return <PictureAsPdfTwoToneIcon />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileType)) return <ImageTwoToneIcon />;
    if (['doc', 'docx'].includes(fileType)) return <DescriptionTwoToneIcon />;
    return <InsertDriveFileTwoToneIcon />;
};

const fileToPurchaseOrderResource = (file: File, index = 0): PurchaseOrderResource => ({
    id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
    name: file.name,
    type: file.type || getExtensionFromFileName(file.name) || 'Unknown',
    file,
});

const normalizePurchaseOrderResources = (resources: any): PurchaseOrderResource[] => {
    const rawResources = Array.isArray(resources)
        ? resources
        : resources && typeof resources === 'object'
            ? Object.values(resources)
            : [];

    return rawResources
        .filter(Boolean)
        .map((resource: any, index: number) => {
            if (typeof File !== 'undefined' && resource instanceof File) return fileToPurchaseOrderResource(resource, index);

            const name = resource?.FileName ?? resource?.Name ?? resource?.ResourceName ?? resource?.OriginalFileName ?? resource?.fileName ?? resource?.name ?? `Resource ${index + 1}`;
            const type = resource?.FileType ?? resource?.ContentType ?? resource?.MimeType ?? resource?.type ?? getExtensionFromFileName(name) ?? 'Unknown';

            return {
                id: String(resource?.PurchaseOrderResourceId ?? resource?.ResourceId ?? resource?.Id ?? resource?.id ?? `${name}-${index}`),
                name,
                type,
                source: resource,
                invoiceCharges: normalizePurchaseOrderInvoiceCharges(resource?.InvoiceCharges ?? resource?.invoiceCharges ?? resource?.Charges ?? resource?.charges),
                invoiceRawJson: resource?.InvoiceAiJson ?? resource?.invoiceRawJson ?? resource?.AiInvoiceJson ?? null,
            };
        });
};


const defaultInitialValues = {};

const EditPurchaseOrderWithAI = () => {
    const [editId, PageName] = FormHelper.getEditPageConfig('Purchase Order');
    const [result, submit] = useSubmitPurchaseOrder();
    useRedirectOnCreate(result, editId, "PurchaseOrderId", "/purchasing/purchase-order/edit?id=")
    const initialValues: any = usePurchaseOrderForm(editId, defaultInitialValues, result);

    const [supplierId, setSupplierId] = useState<number | string | null>(
        initialValues?.Supplier ?? initialValues?.SupplierId ?? null
    );

    const supplierValues = useSupplierForm(supplierId ? String(supplierId) : null, null);
    //console.log('Supplier Values:', supplierValues);
    const applySupplierDefaults = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
        if (!supplierValues) return;
        const patch: Record<string, any> = {
            ShippingMethod: supplierValues?.ShippingMethodId ?? null,
            ShippingTerm: supplierValues?.ShippingTermId ?? null,
            PaymentMethodId: supplierValues?.PaymentMethodId ?? null,
            PaymentTermId: supplierValues?.PaymentTermId ?? null,
            SendType: supplierValues?.SendTypeId ?? null,
            SendDestination: supplierValues?.SendDestination ?? '',
            ShippingTermsDestination: supplierValues?.ShippingTermsDestination ?? '',
            DefaultCurrency_Currency: supplierValues?.CurrencyId ?? null,

        };

        Object.entries(patch).forEach(([field, value]) => {
            if (value !== undefined && value !== null) setFieldValue(field, value);
        });
    };


    useEffect(() => {
        setSupplierId(initialValues?.Supplier ?? initialValues?.SupplierId ?? null);
    }, [initialValues?.Supplier, initialValues?.SupplierId]);

    const [
        purchaseOrderStatusOptions, baseCurrencyOptions, baseCurrencyExchangeRatesOptions, currenciesOptions, recipientsOptions, paymentTermsOptions,
        shippingTermsOptions, paymentMethodsOptions, shippingMethodsOptions, sendTypesOptions, countriesOptions, supplierOptions
    ] = usePurchaseOrderOptions();

    const [recipientId, setRecipientId] = useState<number | string | null>(initialValues?.Recipient ?? null);
    ////console.log("Initial Values:", initialValues, "RecipientId:", recipientId);
    useEffect(() => {
        setRecipientId(initialValues?.Recipient ?? null);
    }, [initialValues?.Recipient]);

    const rowsChangedRef = useRef(false);
    const [orderRowsColumns, orderRowsTableSettings, orderRows, orderRowsSelector, getRowsForSubmit] =
        useSelectProductsOnPurchaseOrder(initialValues, supplierId, result, { onRowsChanged: () => { rowsChangedRef.current = true; } });

    const isExternalWarehouse = useMemo(() => {
        const selected = (recipientsOptions as Array<{ value: any; label: string; warehouseTypeId?: number }>)
            ?.find(o => o.value === recipientId);
        //console.log("Selected Recipient:", selected);
        return (selected?.warehouseTypeId ?? null) === 5; // 5 = External Warehouse
    }, [recipientId, recipientsOptions]);

    const env = useMemo(() => ({
        isExternalWarehouse,
        hasAnyReceivingStartedForRow: (rowId: number) =>
            !!(orderRows || []).find(r => (r?.PurchaseOrderRowId ?? 0) === rowId && (r?.QuantityReceived ?? 0) > 0),
    }), [isExternalWarehouse, orderRows]);

    const policy = useMemo(() => createPurchaseOrderPolicy({
        statusId: initialValues?.Status ?? initialValues?.StatusId ?? null,
        rows: orderRows,
        env,
    }), [initialValues?.Status, initialValues?.StatusId, orderRows, env]);

    // console.log("IsExternalWarehouse:", isExternalWarehouse);
    console.log(initialValues)

    const nextStatusRef = useRef<number | null>(null);
    const formDirtyRef = useRef(false);

    const tabs: CardTab[] = [
        { value: 'Order Rows', label: 'Purchase Order Rows' },
        { value: 'Meta-Data', label: 'Meta-Data' },
        { value: 'Delivery Address', label: 'Delivery Address' },
        { value: 'Billing Address', label: 'Billing Address' },
        { value: 'Supplier Address', label: 'Supplier Address' },
        { value: 'Resources', label: 'Resources' }
    ];
    const [currentTab, setCurrentTab] = useState<string>('Order Rows');
    const resourceInputRef = useRef<HTMLInputElement | null>(null);
    const [resources, setResources] = useState<PurchaseOrderResource[]>([]);
    const [invoiceAiConfig] = usePurchaseOrderInvoiceAiConfig();

    useEffect(() => {
        setResources(normalizePurchaseOrderResources(initialValues?.Resources));
    }, [initialValues?.Resources]);

    const runPurchaseOrderInvoiceAiExtraction = useCallback(async (targetResources: PurchaseOrderResource[]) => {
        const runnablePdfResources = targetResources.filter(resource => isPurchaseOrderInvoiceAiPdfResource(resource) && Boolean(resource.file));

        if (!runnablePdfResources.length) {
            return;
        }

        const targetResourceIds = new Set(runnablePdfResources.map(resource => resource.id));
        setResources(previousResources => previousResources.map(resource => targetResourceIds.has(resource.id) ? { ...resource, invoiceAiStatus: 'pending', invoiceAiError: undefined } : resource));

        try {
            if (!invoiceAiConfig.url.trim()) {
                throw new Error("AI API URL is missing.");
            }

            const attachments = await readPurchaseOrderInvoiceAiAttachmentsFromResources(runnablePdfResources);
            const responseText = await requestPurchaseOrderInvoiceAiResponse({
                config: invoiceAiConfig,
                promptText: buildPurchaseOrderInvoiceAiPrompt(purchaseOrderInvoiceExtractionDefaultPrompt, runnablePdfResources),
                attachments,
            });

            const extractions = parsePurchaseOrderInvoiceExtractionResponse(responseText, runnablePdfResources);

            if (!extractions.length) {
                throw new Error("The AI response did not contain any invoice Charges.");
            }

            setResources(previousResources => applyPurchaseOrderInvoiceExtractionsToResources(previousResources, extractions, runnablePdfResources));
        } catch (error) {
            console.error("Failed to run purchase order invoice AI extraction.", error);
            const message = error instanceof Error ? error.message : "Could not run the AI request.";
            setResources(previousResources => previousResources.map(resource => targetResourceIds.has(resource.id) ? { ...resource, invoiceAiStatus: 'error', invoiceAiError: message } : resource));
        }
    }, [invoiceAiConfig]);

    const addResourceFiles = (files?: FileList | File[] | null) => {
        const selectedFiles = Array.from(files ?? []);
        if (!selectedFiles.length) return;

        const nextResources = selectedFiles.map((file, index) => fileToPurchaseOrderResource(file, resources.length + index));
        setResources(prev => [...prev, ...nextResources]);
        void runPurchaseOrderInvoiceAiExtraction(nextResources);
    };

    const handleResourceDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        addResourceFiles(event.dataTransfer.files);
    };

    const removeResource = (resourceId: string) => {
        setResources(prev => prev.filter(resource => resource.id !== resourceId));
    };



    return (
        <>
            <Formik
                initialValues={initialValues}
                validateOnBlur={true}
                validateOnChange={false}
                validationSchema={scheme}
                enableReinitialize
                onSubmit={(values: any, formikHelpers: FormikHelpers<any>) => {
                    const requested = nextStatusRef.current;
                    nextStatusRef.current = null; // consume it

                    const apiRef = orderRowsTableSettings?.apiRef;
                    const rowModels = apiRef?.current?.getRowModels?.();
                    const gridRows = rowModels ? Array.from(rowModels.values()) : null;



                    const rowsForSubmit = getRowsForSubmit();

                    submit(values, rowsForSubmit, formikHelpers, {
                        nextStatusId: requested ?? undefined,
                        guards: { formDirty: formDirtyRef.current, rowsChanged: rowsChangedRef.current },
                        baselineRows: initialValues?.PurchaseOrderRows ?? [],
                    });
                }}

            >
                {(props): JSX.Element => (
                    <div>
                        {useEffect(() => {
                            if (props.values?.Supplier !== supplierId) {
                                setSupplierId(props.values?.Supplier ?? null);
                            }
                        }, [props.values?.Supplier])}

                        <PageHeader
                            title={PageName}
                            initialValues={initialValues}
                            subTitle="Create and edit Purchase Order"
                            video={[
                                { title: "New Purchase Order", url: "https://www.youtube.com/embed/M-7nC8Fj-2s?si=lrDV6xAspJrWiD12" },
                            ]}
                            components={[
                                {
                                    component: (
                                        <CustomButton
                                            isSubmitting={props.isSubmitting}
                                            onClick={() => {
                                                nextStatusRef.current = null; // plain save

                                                props.handleSubmit();
                                            }}
                                            title="Save"
                                            icon={<SaveIcon />}
                                        />
                                    )
                                },
                                {
                                    component: (
                                        <CustomButton
                                            title="Send"
                                            variant="outlined"
                                            icon={<SendTwoToneIcon />}
                                            onClick={() => {
                                                nextStatusRef.current = STATUS_ID.Sent;
                                                formDirtyRef.current = props.dirty;
                                                props.handleSubmit();
                                            }}
                                            disabled={
                                                props.isSubmitting ||
                                                !policy.action("Send") ||
                                                !props.values?.PurchaseOrderId // require existing order id
                                            }
                                        />
                                    )
                                },
                                {
                                    component: (
                                        <CustomButton
                                            title="Confirm"
                                            variant="outlined"
                                            icon={<ThumbUpIcon />}
                                            onClick={() => {
                                                nextStatusRef.current = STATUS_ID.Confirmed;
                                                formDirtyRef.current = props.dirty;
                                                props.handleSubmit();
                                            }}
                                            disabled={
                                                props.isSubmitting ||
                                                !policy.action("SetConfirmed") ||
                                                !props.values?.PurchaseOrderId
                                            }
                                        />
                                    )
                                },
                                {
                                    component: (
                                        <CustomButton
                                            title={policy.action("SetConfirmed") ? "Confirm & Ship" : "Ship"}
                                            variant="outlined"
                                            icon={<LocalShippingTwoToneIcon />}
                                            onClick={() => {
                                                nextStatusRef.current = STATUS_ID.Shipped;
                                                formDirtyRef.current = props.dirty;
                                                props.handleSubmit();
                                            }}
                                            disabled={
                                                props.isSubmitting ||
                                                !policy.action("SetShipped") ||
                                                !props.values?.PurchaseOrderId
                                            }
                                        />
                                    )
                                },

                                policy.action("SetReceivedShort") ? {
                                    component: (
                                        <CustomButton
                                            title="Recieve Short"
                                            variant="outlined"
                                            color="error"
                                            icon={<CancelTwoToneIcon />}
                                            onClick={() => {
                                                nextStatusRef.current = STATUS_ID['Received Short'];
                                                formDirtyRef.current = false;
                                                props.handleSubmit();
                                            }}
                                            disabled={
                                                props.isSubmitting ||
                                                !policy.action("SetReceivedShort") ||
                                                !props.values?.PurchaseOrderId
                                            }
                                        />
                                    )
                                } :
                                    {
                                        component: (
                                            <CustomButton
                                                title="Cancel"
                                                variant="outlined"
                                                color="error"
                                                icon={<CancelTwoToneIcon />}
                                                onClick={() => {
                                                    nextStatusRef.current = STATUS_ID.Cancelled;
                                                    formDirtyRef.current = false;
                                                    props.handleSubmit();
                                                }}
                                                disabled={
                                                    props.isSubmitting ||
                                                    !policy.action("Cancel") ||
                                                    !props.values?.PurchaseOrderId
                                                }
                                            />
                                        )
                                    },
                                { isInGroupedMenu: true, component: <CustomButton variant="text" title="New Purchase Order" icon={<AddTwoToneIcon />} onClick={() => { window.location.href = '/purchasing/purchase-order/create'; }} /> },
                                { isInGroupedMenu: true, component: <PurchaseOrderInvoiceAiCopyPromptButton resources={resources} /> },
                                { isInGroupedMenu: true, component: <PurchaseOrderInvoiceAiConfigButton /> }
                            ]}
                            alertData={{ dataArray: result ? [result] : null }}
                        />

                        <input
                            ref={resourceInputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                addResourceFiles(event.target.files);
                                event.target.value = '';
                            }}
                        />

                        <ContentCardsContainer>
                            <ContentCard cardId="Order Info" md={12}>
                                <FormContainerMolecule formikProps={props}>
                                    <FormField
                                        fieldProps={{
                                            name: 'Supplier',
                                            select: true,
                                            label: 'Supplier',
                                            md: 4.8,
                                            disabled: !policy.field("Meta.Supplier")
                                        }}
                                        formikProps={props}
                                    >
                                        {supplierOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>
                                    <Grid item md={1} sx={{ alignSelf: "center" }}>
                                        <CustomButton iconButton size="small" title="Apply supplier defaults" icon={<AutoFixHighTwoToneIcon />} onClick={() => applySupplierDefaults(props.setFieldValue)} disabled={!supplierValues} />
                                    </Grid>



                                    {/* Status (read-only) */}
                                    <FormField fieldProps={{ name: 'Status', select: true, label: 'Status', md: 5.8, disabled: true }} formikProps={props}>
                                        {purchaseOrderStatusOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>
                                    {/* <FormField fieldProps={{ name: 'PurchaseOrderType', label: 'Purchase Order Type', md: 0.8 }} formikProps={props} /> */}
                                    {/* Supplier + "apply defaults" button */}
                                    <FormField fieldProps={{ name: 'ExternalOrderId', disabled: true, label: 'External Order Id', md: 3.8 }} formikProps={props} />



                                    <FormField fieldProps={{ name: 'SupplierContact', label: 'Supplier Contact', md: 3.8 }} formikProps={props} />
                                    <FormField fieldProps={{ name: 'Recipient', select: true, label: 'Recipient', md: 2.8 }} formikProps={props}>
                                        {recipientsOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>


                                </FormContainerMolecule>
                            </ContentCard>


                            <ContentCard cardId="Payment Info" md={6}>
                                <FormContainerMolecule formikProps={props}>
                                    <FormField fieldProps={{ name: 'PaymentMethodId', select: true, label: 'Payment Method', md: 3.8 }} formikProps={props}>
                                        {paymentMethodsOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>
                                    <FormField fieldProps={{ name: 'PaymentTermId', select: true, label: 'Payment Term', md: 3.8 }} formikProps={props}>
                                        {paymentTermsOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>

                                    <FormField
                                        fieldProps={{
                                            name: 'OrderValueInDefaultCurrency', label: 'Order Value (Default Currency)', md: 3.8
                                        }}
                                        formikProps={props}
                                    />
                                    <FormField
                                        fieldProps={{
                                            name: 'OrderValueIncMiscCharges', label: 'Order Value (Inc. Misc Charges)', md: 3.8
                                        }}
                                        formikProps={props}
                                    />
                                    <FormField
                                        fieldProps={{
                                            name: 'CurrencyExchangeRate', label: 'Currency Exchange Rate', md: 3.8,
                                            disabled: !policy.field("Meta.CurrencyExchangeRate")
                                        }}
                                        formikProps={props}
                                    />
                                    <FormField
                                        fieldProps={{
                                            name: 'DefaultCurrency_Currency', select: true, disabled: true, label: 'Default Currency', md: 3.8
                                        }}
                                        formikProps={props}
                                    >
                                        {currenciesOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>
                                </FormContainerMolecule>
                            </ContentCard>

                            <ContentCard cardId="Shipping Info" md={6}>
                                <FormContainerMolecule formikProps={props}>
                                    <FormField
                                        fieldProps={{
                                            name: 'ShippingMethod', select: true, label: 'Shipping Method', md: 3.8,
                                            disabled: !policy.field("Meta.ShippingMethod")
                                        }}
                                        formikProps={props}
                                    >
                                        {shippingMethodsOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>

                                    <FormField
                                        fieldProps={{
                                            name: 'ShippingTerm', select: true, label: 'Shipping Term', md: 3.8,
                                            disabled: !policy.field("Meta.ShippingTerms")
                                        }}
                                        formikProps={props}
                                    >
                                        {shippingTermsOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>

                                    <FormField
                                        fieldProps={{
                                            name: 'ShippingTermsDestination', label: 'Shipping Term Destination', md: 3.8,
                                            disabled: !policy.field("Meta.ShippingTermsDestination")
                                        }}
                                        formikProps={props}
                                    />
                                    <FormField
                                        fieldProps={{
                                            name: 'SendType', select: true, label: 'Send Type', md: 3.8,
                                            disabled: !policy.field("Meta.SendType")
                                        }}
                                        formikProps={props}
                                    >
                                        {sendTypesOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                    </FormField>
                                    <FormField
                                        fieldProps={{
                                            name: 'SendDestination', label: 'Send Destination', md: 3.8,
                                            disabled: !policy.field("Meta.SendDestination")
                                        }}
                                        formikProps={props}
                                    />
                                </FormContainerMolecule>
                            </ContentCard>

                            <ContentCard
                                {...{
                                    cardId: "TabContainer",
                                    contentPadding: 0,
                                    cardTypes: [CardType.tabContainer],
                                    cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} />,
                                    components: [
                                        {
                                            component: (
                                                <CustomButton
                                                    onClick={() => orderRowsSelector?.openSelector()}
                                                    title="Add Row"
                                                    icon={<AddTwoToneIcon />}
                                                    disabled={!policy.action("Add_Row")}
                                                />
                                            )
                                        },
                                    ],
                                }}
                            >
                                {currentTab === 'Order Rows' && (
                                    <>
                                        <SelectTableDialog {...orderRowsSelector} />
                                        <ContentCard cardId="Order Rows" cardTypes={[CardType.tab]} contentPadding={0}>
                                            <CustomDataGrid columns={orderRowsColumns} tableSettings={orderRowsTableSettings} />
                                        </ContentCard>
                                    </>
                                )}

                                {currentTab === 'Meta-Data' && (
                                    <ContentCard cardId="Meta-Data" cardTypes={[CardType.tab]} md={12}>
                                        <FormContainerMolecule formikProps={props}>
                                            <FormField fieldProps={{ name: 'CreateDate', date: true, disabled: true, label: 'Create Date', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'CreatedBy', disabled: true, label: 'Created By', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'ModifyDate', date: true, disabled: true, label: 'Modify Date', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'ModifiedBy', disabled: true, label: 'Modified By', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'SendDate', date: true, disabled: true, label: 'Send Date', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'ReceiveDate', date: true, disabled: true, label: 'Receive Date', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'OurContact', label: 'Our Contact', md: 5.8 }} formikProps={props} />
                                            <FormField
                                                fieldProps={{
                                                    name: 'Comment', label: 'Comment', md: 12,
                                                    disabled: !policy.field("Meta.Comment")
                                                }}
                                                formikProps={props}
                                            />
                                        </FormContainerMolecule>
                                    </ContentCard>
                                )}

                                {currentTab === 'Delivery Address' && (
                                    <ContentCard cardId="Delivery Address" cardTypes={[CardType.tab]} md={12}>
                                        <FormContainerMolecule formikProps={props}>
                                            {/* unchanged; address edits not covered by this sheet */}
                                            <FormField fieldProps={{ name: 'DeliveryAddress', disabled: true, label: 'Delivery Address Id', md: 5.8 }} formikProps={props} />
                                            <FormField fieldProps={{ name: 'DeliveryAddress_Company', label: 'Company', md: 5.8 }} formikProps={props} />
                                            {/* ... the rest of your fields */}
                                        </FormContainerMolecule>
                                    </ContentCard>
                                )}

                                {currentTab === 'Billing Address' && (
                                    <ContentCard cardId="Billing Address" cardTypes={[CardType.tab]} md={12}>
                                        <FormContainerMolecule formikProps={props}>
                                            {/* unchanged */}
                                            <FormField fieldProps={{ name: 'BillingAddress', disabled: true, label: 'Billing Address Id', md: 5.8 }} formikProps={props} />
                                            {/* ... */}
                                        </FormContainerMolecule>
                                    </ContentCard>
                                )}

                                {currentTab === 'Supplier Address' && (
                                    <ContentCard cardId="Supplier Address" cardTypes={[CardType.tab]} md={12}>
                                        <FormContainerMolecule formikProps={props}>
                                            {/* unchanged */}
                                            <FormField fieldProps={{ name: 'SupplierAddress', disabled: true, label: 'Supplier Address Id', md: 5.8 }} formikProps={props} />
                                            {/* ... */}
                                        </FormContainerMolecule>
                                    </ContentCard>
                                )}

                                {currentTab === 'Resources' && (
                                    <ContentCard cardId="Resources" cardTypes={[CardType.tab]} md={12}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 }, alignSelf: 'flex-start' }}>
                                                <Box
                                                    onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
                                                    onDrop={handleResourceDrop}
                                                    sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default', maxHeight: 300, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, overflow: 'auto' }}
                                                >
                                                    <Grid container spacing={1} justifyContent="center" flexDirection={"column"} alignItems="center">
                                                        <Grid item>

                                                            <CustomButton onClick={() => resourceInputRef.current?.click()} title="Add File" icon={<UploadFileOutlined />} />
                                                        </Grid>
                                                        <Grid item>
                                                            <UploadFileOutlined fontSize="large" />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant="h6">Drag and drop files here</Typography>
                                                        </Grid>

                                                    </Grid>

                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
                                                <List dense sx={{ p: 0 }}>
                                                    {resources.length ? resources.map(resource => (
                                                        <ListItem
                                                            key={resource.id}
                                                            divider
                                                            secondaryAction={(
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Chip size="small" label={getResourceTypeLabel(resource)} />
                                                                    {isPurchaseOrderInvoiceAiPdfResource(resource) ? (
                                                                        <CustomButton iconButton size="small" title="Re-run AI extraction" icon={<ReplayTwoToneIcon />} onClick={() => void runPurchaseOrderInvoiceAiExtraction([resource])} disabled={resource.invoiceAiStatus === 'pending' || !resource.file} />
                                                                    ) : null}
                                                                    <CustomButton iconButton size="small" title="Remove" color="error" icon={<DeleteTwoToneIcon />} onClick={() => removeResource(resource.id)} />
                                                                </Box>
                                                            )}
                                                        >
                                                            <ListItemIcon>{getResourceIcon(resource)}</ListItemIcon>
                                                            <ListItemText primary={resource.name} secondary={<PurchaseOrderResourceInvoiceData resource={resource} />} secondaryTypographyProps={{ component: 'div' }} />
                                                        </ListItem>
                                                    )) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No resources uploaded.</Typography>
                                                    )}
                                                </List>
                                            </Grid>
                                        </Grid>
                                    </ContentCard>
                                )}
                            </ContentCard>
                        </ContentCardsContainer>
                    </div>
                )}
            </Formik>
            <Footer />
        </>
    );
};

export default EditPurchaseOrderWithAI;






/* ========================= PURCHASE ORDER INVOICE AI PROTOTYPE ========================= */

type PurchaseOrderInvoiceAiConfig = {
    url: string;
    apiKey: string;
    model: string;
    promptCacheKey: string;
    promptCacheRetention: string;
};

type PurchaseOrderInvoiceAiAttachment = {
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
    dataUrl: string;
};

type PurchaseOrderInvoiceAiMessageContentPart =
    | { type: "text"; text: string }
    | { type: "file"; file: { filename: string; file_data: string } };

type PurchaseOrderInvoiceAiMessageContent = string | PurchaseOrderInvoiceAiMessageContentPart[];

type PurchaseOrderInvoiceAiChatMessage = {
    role: "system" | "user";
    content: PurchaseOrderInvoiceAiMessageContent;
};

const purchaseOrderInvoiceAiConfigStorageKey = "purchaseOrderInvoiceAiConfig";
const purchaseOrderInvoiceAiConfigChangedEvent = "purchaseOrderInvoiceAiConfigChanged";
const purchaseOrderInvoiceAiPdfAttachmentMaxBytes = 50 * 1024 * 1024;

const purchaseOrderInvoiceAiModelOptions = [
    "gpt-5.5",
    "gpt-5.4-mini",
    "gpt-5.4-nano",
];

const purchaseOrderInvoiceAiModelOptionMeta: Record<string, { group: string; description: string }> = {
    "gpt-5.5": { group: "Frontier", description: "Best current frontier model for complex invoice extraction." },
    "gpt-5.4-mini": { group: "Cost-efficient", description: "Balanced model for PDFs, vision, and structured JSON." },
    "gpt-5.4-nano": { group: "Cost-efficient", description: "Lowest-latency option for simpler invoices." },
};

const purchaseOrderInvoiceAiDefaultConfig: PurchaseOrderInvoiceAiConfig = {
    url: "https://api.openai.com/v1/chat/completions",
    apiKey: "",
    model: "gpt-5.4-mini-2026-03-17",
    promptCacheKey: "dacsa-purchase-order-invoice-charges-v1",
    promptCacheRetention: "",
};

const purchaseOrderInvoiceAiSystemPrompt = "You extract invoice charges for a purchase order prototype. Return only valid JSON. Do not return markdown, comments, explanations, or fields outside the specified schema.";

const purchaseOrderInvoiceExtractionDefaultPrompt = `
Extract invoice charges from the attached invoice PDF files.

Return only valid JSON in this exact shape:
{
  "Invoices": [
    {
      "FileName": "invoice-file-name.pdf",
      "Charges": [
        { "ChargeType": "Total", "Sum": 123.45, "Currency": "EUR" },
        { "ChargeType": "Goods", "Sum": 100.00, "Currency": "EUR" },
        { "ChargeType": "Shipping", "Sum": 23.45, "Currency": "EUR" }
      ]
    }
  ]
}

Allowed ChargeType string values:
- Total
- Goods
- Packaging
- Handling
- Shipping
- Customs

Rules:
- ChargeType must be a string and must exactly match one of the allowed values, for example "ChargeType": "Total".
- Sum must be the numeric invoice amount for that charge, with no currency symbol.
- Currency must be the ISO currency code shown on the invoice, for example EUR, USD, SEK, GBP, DKK or NOK.
- Some charge types may not exist on the invoice. Omit missing charge types completely.
- Only include charges that are visible on the invoice or directly stated as totals/subtotals. Do not invent missing charges.
- Use the exact FileName values supplied in the resource list.
- If several PDFs are attached, return one Invoices item per PDF that contains invoice charges.
`.trim();

function PurchaseOrderInvoiceAiCopyPromptButton({ resources }: { resources: PurchaseOrderResource[] }) {
    const [buttonTitle, setButtonTitle] = useState("Copy PDF AI Prompt");

    const handleCopy = async (event?: any) => {
        event?.preventDefault();

        try {
            await writePurchaseOrderInvoiceAiTextToClipboard(buildPurchaseOrderInvoiceAiPrompt(purchaseOrderInvoiceExtractionDefaultPrompt, resources));
            setButtonTitle("Copied");
            window.setTimeout(() => setButtonTitle("Copy AI Prompt"), 1800);
        } catch (error) {
            console.error("Failed to copy purchase order invoice AI prompt.", error);
            setButtonTitle("Copy failed");
            window.setTimeout(() => setButtonTitle("Copy AI Prompt"), 1800);
            window.alert("Could not copy the invoice extraction prompt to the clipboard.");
        }
    };

    return <CustomButton variant="text" title={buttonTitle} icon={<ContentCopyIcon />} onClick={handleCopy} />;
}

function PurchaseOrderInvoiceAiConfigButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Config" icon={<SettingsIcon />} onClick={(event?: any) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <PurchaseOrderInvoiceAiConfigDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}

function PurchaseOrderInvoiceAiConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [config, setConfig] = usePurchaseOrderInvoiceAiConfig();
    const [draftConfig, setDraftConfig] = useState<PurchaseOrderInvoiceAiConfig>(config);

    useEffect(() => {
        if (open) {
            setDraftConfig(config);
        }
    }, [config, open]);

    const handleChange = (fieldName: keyof PurchaseOrderInvoiceAiConfig) => (event: ChangeEvent<HTMLInputElement>) => {
        setDraftConfig((previousConfig) => ({ ...previousConfig, [fieldName]: event.target.value }));
    };

    const handleSave = () => {
        setConfig({
            url: draftConfig.url.trim() || purchaseOrderInvoiceAiDefaultConfig.url,
            apiKey: draftConfig.apiKey.trim(),
            model: draftConfig.model.trim() || purchaseOrderInvoiceAiDefaultConfig.model,
            promptCacheKey: draftConfig.promptCacheKey.trim(),
            promptCacheRetention: draftConfig.promptCacheRetention.trim(),
        });
        onClose();
    };

    const handleReset = () => {
        setDraftConfig(purchaseOrderInvoiceAiDefaultConfig);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>AI configuration</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="API URL"
                        value={draftConfig.url}
                        onChange={handleChange("url")}
                        fullWidth
                        helperText="Include protocol and port if needed, for example http://localhost:11434/v1/chat/completions."
                    />
                    <TextField
                        label="API key"
                        value={draftConfig.apiKey}
                        onChange={handleChange("apiKey")}
                        fullWidth
                        type="password"
                        autoComplete="off"
                        helperText="Stored in localStorage. Leave empty for local endpoints that do not require a key."
                    />
                    <Autocomplete
                        freeSolo
                        autoHighlight
                        options={purchaseOrderInvoiceAiModelOptions}
                        value={draftConfig.model}
                        inputValue={draftConfig.model}
                        groupBy={(option) => purchaseOrderInvoiceAiModelOptionMeta[option]?.group ?? "Custom"}
                        onInputChange={(_, nextInputValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextInputValue }));
                        }}
                        onChange={(_, nextValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextValue ?? "" }));
                        }}
                        renderOption={(props, option) => {
                            const meta = purchaseOrderInvoiceAiModelOptionMeta[option];

                            return (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Box component="span" fontWeight={600}>{option}</Box>
                                        {meta?.description ? (
                                            <Box component="span" color="text.secondary" fontSize="0.8rem" ml={1}>
                                                {meta.description}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model"
                                fullWidth
                                helperText="Choose a common OpenAI model or type any OpenAI-compatible model name."
                            />
                        )}
                    />
                    <TextField
                        label="Prompt cache key"
                        value={draftConfig.promptCacheKey}
                        onChange={handleChange("promptCacheKey")}
                        fullWidth
                        helperText="Sent to OpenAI-compatible OpenAI URLs as prompt_cache_key. Keep stable per page/entity version."
                    />
                    <TextField
                        label="Prompt cache retention"
                        value={draftConfig.promptCacheRetention}
                        onChange={handleChange("promptCacheRetention")}
                        fullWidth
                        placeholder="Optional, for example 24h"
                        helperText="Optional OpenAI prompt_cache_retention. Leave empty to use the API default. Not sent to local llama.cpp URLs."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset}>Reset defaults</Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function PurchaseOrderResourceInvoiceData({ resource }: { resource: PurchaseOrderResource }) {
    const charges = normalizePurchaseOrderInvoiceCharges(resource.invoiceCharges ?? []);

    if (resource.invoiceAiStatus === 'pending') {
        return (
            <Box sx={{ mt: 0.75 }}>
                <Typography variant="caption" color="text.secondary">Extracting invoice charges...</Typography>
            </Box>
        );
    }

    if (resource.invoiceAiStatus === 'error') {
        return (
            <Box sx={{ mt: 0.75 }}>
                <Alert severity="error" sx={{ py: 0 }}>{resource.invoiceAiError ?? "Could not extract invoice charges."}</Alert>
            </Box>
        );
    }

    if (!charges.length) {
        return null;
    }

    return (
        <Box sx={{ mt: 0.75 }}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
                {charges.map((charge, index) => (
                    <Chip
                        key={`${resource.id}-${charge.ChargeType}-${index}`}
                        size="small"
                        variant="outlined"
                        label={`${charge.ChargeType}: ${formatPurchaseOrderInvoiceChargeSum(charge.Sum)} ${charge.Currency}`}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function usePurchaseOrderInvoiceAiConfig(): [PurchaseOrderInvoiceAiConfig, (nextConfig: PurchaseOrderInvoiceAiConfig) => void] {
    const [config, setConfigState] = useState<PurchaseOrderInvoiceAiConfig>(() => getStoredPurchaseOrderInvoiceAiConfig());

    const setConfig = useCallback((nextConfig: PurchaseOrderInvoiceAiConfig) => {
        setConfigState(nextConfig);
        saveStoredPurchaseOrderInvoiceAiConfig(nextConfig);
    }, []);

    useEffect(() => {
        const refreshConfig = () => setConfigState(getStoredPurchaseOrderInvoiceAiConfig());
        const handleStorage = (event: StorageEvent) => {
            if (event.key === purchaseOrderInvoiceAiConfigStorageKey) {
                refreshConfig();
            }
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener(purchaseOrderInvoiceAiConfigChangedEvent, refreshConfig);

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener(purchaseOrderInvoiceAiConfigChangedEvent, refreshConfig);
        };
    }, []);

    return [config, setConfig];
}

function getStoredPurchaseOrderInvoiceAiConfig(): PurchaseOrderInvoiceAiConfig {
    if (typeof window === "undefined") {
        return purchaseOrderInvoiceAiDefaultConfig;
    }

    try {
        const rawConfig = window.localStorage.getItem(purchaseOrderInvoiceAiConfigStorageKey);
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : {};

        return {
            ...purchaseOrderInvoiceAiDefaultConfig,
            ...parsedConfig,
            url: parsedConfig?.url || purchaseOrderInvoiceAiDefaultConfig.url,
            apiKey: parsedConfig?.apiKey || "",
            model: parsedConfig?.model || purchaseOrderInvoiceAiDefaultConfig.model,
            promptCacheKey: parsedConfig?.promptCacheKey ?? purchaseOrderInvoiceAiDefaultConfig.promptCacheKey,
            promptCacheRetention: parsedConfig?.promptCacheRetention ?? purchaseOrderInvoiceAiDefaultConfig.promptCacheRetention,
        };
    } catch (error) {
        console.error("Failed to load purchase order invoice AI config from localStorage.", error);
        return purchaseOrderInvoiceAiDefaultConfig;
    }
}

function saveStoredPurchaseOrderInvoiceAiConfig(config: PurchaseOrderInvoiceAiConfig) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(purchaseOrderInvoiceAiConfigStorageKey, JSON.stringify(config));
    window.dispatchEvent(new Event(purchaseOrderInvoiceAiConfigChangedEvent));
}

async function requestPurchaseOrderInvoiceAiResponse({
    config,
    promptText,
    attachments,
}: {
    config: PurchaseOrderInvoiceAiConfig;
    promptText: string;
    attachments: PurchaseOrderInvoiceAiAttachment[];
}) {
    const messages = buildPurchaseOrderInvoiceAiMessages({
        systemPrompt: purchaseOrderInvoiceAiSystemPrompt,
        promptText,
        attachments,
    });

    const response = await fetch(config.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(config.apiKey ? { Authorization: `Bearer ${config.apiKey}` } : {}),
        },
        body: JSON.stringify(getPurchaseOrderInvoiceAiRequestBody(config, purchaseOrderInvoiceAiDefaultConfig.model, messages)),
    });

    const responseBodyText = await response.text();

    if (!response.ok) {
        throw new Error(getPurchaseOrderInvoiceAiApiErrorMessage(response.status, responseBodyText));
    }

    const responseBody = safePurchaseOrderInvoiceAiJsonParse(responseBodyText);
    const content = getPurchaseOrderInvoiceAiResponseContent(responseBody) ?? responseBodyText;
    const jsonText = extractPurchaseOrderInvoiceAiJsonObjectText(content);

    if (!jsonText || jsonText === content.trim() && !content.trim().startsWith("{") && !content.trim().startsWith("[") && !content.trim().startsWith("```")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function getPurchaseOrderInvoiceAiRequestBody(config: PurchaseOrderInvoiceAiConfig, defaultModel: string, messages: PurchaseOrderInvoiceAiChatMessage[]) {
    const isOpenAiApiUrl = isPurchaseOrderInvoiceAiOpenAiApiUrl(config.url);
    const promptCacheKey = config.promptCacheKey.trim();
    const promptCacheRetention = config.promptCacheRetention.trim();
    const requestBody: any = {
        model: config.model || defaultModel,
        messages,
        temperature: 1,
        stream: false,
    };

    if (isOpenAiApiUrl) {
        if (promptCacheKey) {
            requestBody.prompt_cache_key = promptCacheKey;
        }

        if (promptCacheRetention) {
            requestBody.prompt_cache_retention = promptCacheRetention;
        }
    } else {
        requestBody.cache_prompt = true;
    }

    return requestBody;
}

function buildPurchaseOrderInvoiceAiMessages({
    systemPrompt,
    promptText,
    attachments,
}: {
    systemPrompt: string;
    promptText: string;
    attachments: PurchaseOrderInvoiceAiAttachment[];
}): PurchaseOrderInvoiceAiChatMessage[] {
    const attachmentParts = attachments.map((attachment) => ({
        type: "file" as const,
        file: {
            filename: attachment.fileName,
            file_data: attachment.dataUrl,
        },
    }));

    return [
        {
            role: "system",
            content: systemPrompt,
        },
        {
            role: "user",
            content: attachmentParts.length > 0
                ? [
                    ...attachmentParts,
                    { type: "text", text: promptText },
                ]
                : promptText,
        },
    ];
}

function buildPurchaseOrderInvoiceAiPrompt(prompt: string, resources: PurchaseOrderResource[]) {
    const pdfResources = resources.filter(resource => isPurchaseOrderInvoiceAiPdfResource(resource));
    const resourceList = pdfResources.length
        ? pdfResources.map((resource, index) => `${index + 1}. ${resource.name} (${getResourceTypeLabel(resource)})`).join("\n")
        : "No PDF resources are currently uploaded.";

    return [
        prompt.trim(),
        "",
        "Resource file list. Use these exact FileName values in the returned JSON:",
        resourceList,
    ].join("\n");
}

function parsePurchaseOrderInvoiceExtractionResponse(responseText: string, targetResources: PurchaseOrderResource[]): PurchaseOrderInvoiceExtraction[] {
    const parsedResponse = parsePurchaseOrderInvoiceAiJsonFromText(responseText);
    const targetPdfResources = targetResources.filter(resource => isPurchaseOrderInvoiceAiPdfResource(resource));

    if (Array.isArray(parsedResponse)) {
        const firstItem = parsedResponse[0];

        if (firstItem?.ChargeType || firstItem?.Type) {
            return targetPdfResources.length === 1
                ? [{ FileName: targetPdfResources[0].name, Charges: normalizePurchaseOrderInvoiceCharges(parsedResponse), RawJson: parsedResponse }]
                : [];
        }

        return parsedResponse.map(item => normalizePurchaseOrderInvoiceExtraction(item)).filter(Boolean) as PurchaseOrderInvoiceExtraction[];
    }

    const invoices = parsedResponse?.Invoices ?? parsedResponse?.invoices ?? parsedResponse?.Resources ?? parsedResponse?.resources ?? parsedResponse?.Files ?? parsedResponse?.files;
    if (Array.isArray(invoices)) {
        return invoices.map((invoice: any) => normalizePurchaseOrderInvoiceExtraction(invoice)).filter(Boolean) as PurchaseOrderInvoiceExtraction[];
    }

    const directCharges = parsedResponse?.Charges ?? parsedResponse?.charges;
    if (Array.isArray(directCharges) && targetPdfResources.length === 1) {
        const charges = normalizePurchaseOrderInvoiceCharges(directCharges);
        return charges.length ? [{ FileName: targetPdfResources[0].name, Charges: charges, RawJson: parsedResponse }] : [];
    }

    const directExtraction = normalizePurchaseOrderInvoiceExtraction(parsedResponse);
    return directExtraction ? [directExtraction] : [];
}

function normalizePurchaseOrderInvoiceExtraction(value: any): PurchaseOrderInvoiceExtraction | null {
    if (!value || typeof value !== "object") {
        return null;
    }

    const FileName = String(value?.FileName ?? value?.fileName ?? value?.Filename ?? value?.filename ?? value?.ResourceName ?? value?.name ?? '').trim();
    const Charges = normalizePurchaseOrderInvoiceCharges(value?.Charges ?? value?.charges ?? value?.InvoiceCharges ?? value?.invoiceCharges);

    if (!Charges.length) {
        return null;
    }

    return {
        FileName: FileName || undefined,
        Charges,
        RawJson: value,
    };
}

function applyPurchaseOrderInvoiceExtractionsToResources(resources: PurchaseOrderResource[], extractions: PurchaseOrderInvoiceExtraction[], targetResources: PurchaseOrderResource[]) {
    const targetResourceIds = new Set(targetResources.map(resource => resource.id));
    const targetPdfResources = targetResources.filter(resource => isPurchaseOrderInvoiceAiPdfResource(resource));
    const extractionsByFileName = new Map<string, PurchaseOrderInvoiceExtraction>();

    extractions.forEach(extraction => {
        if (extraction.FileName) {
            extractionsByFileName.set(normalizePurchaseOrderInvoiceFileName(extraction.FileName), extraction);
        }
    });

    const singleTargetFallbackExtraction = targetPdfResources.length === 1 && extractions.length === 1 ? extractions[0] : null;

    return resources.map(resource => {
        if (!targetResourceIds.has(resource.id)) {
            return resource;
        }

        const extraction = extractionsByFileName.get(normalizePurchaseOrderInvoiceFileName(resource.name)) ?? singleTargetFallbackExtraction;

        if (!extraction) {
            return { ...resource, invoiceAiStatus: 'done' as const, invoiceAiError: undefined };
        }

        return {
            ...resource,
            invoiceCharges: extraction.Charges,
            invoiceRawJson: extraction.RawJson ?? extraction,
            invoiceAiStatus: 'done' as const,
            invoiceAiError: undefined,
        };
    });
}

async function readPurchaseOrderInvoiceAiAttachmentsFromResources(resources: PurchaseOrderResource[]) {
    return Promise.all(resources.map(readPurchaseOrderInvoiceAiAttachmentFromResource));
}

async function readPurchaseOrderInvoiceAiAttachmentFromResource(resource: PurchaseOrderResource): Promise<PurchaseOrderInvoiceAiAttachment> {
    if (!resource.file) {
        throw new Error(`${resource.name} is not available as a browser File object.`);
    }

    if (!isPurchaseOrderInvoiceAiPdfFile(resource.file)) {
        throw new Error(`${resource.name} is not a PDF file.`);
    }

    if (resource.file.size > purchaseOrderInvoiceAiPdfAttachmentMaxBytes) {
        throw new Error(`${resource.name} is larger than ${formatPurchaseOrderInvoiceAiBytes(purchaseOrderInvoiceAiPdfAttachmentMaxBytes)}.`);
    }

    return {
        id: resource.id,
        fileName: resource.name,
        mimeType: resource.file.type,
        size: resource.file.size,
        dataUrl: await readPurchaseOrderInvoiceAiFileAsDataUrl(resource.file),
    };
}

function isPurchaseOrderInvoiceAiPdfResource(resource: PurchaseOrderResource) {
    return getResourceTypeLabel(resource).toLowerCase() === 'pdf';
}

function isPurchaseOrderInvoiceAiPdfFile(file: File) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return mimeType === "application/pdf" || mimeType === "application/x-pdf" || fileName.endsWith(".pdf");
}

function readPurchaseOrderInvoiceAiFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Could not read attachment."));
        reader.readAsDataURL(file);
    });
}

function getPurchaseOrderInvoiceAiResponseContent(responseBody: any) {
    const chatCompletionContent = responseBody?.choices?.[0]?.message?.content;

    if (typeof chatCompletionContent === "string") {
        return chatCompletionContent;
    }

    if (Array.isArray(chatCompletionContent)) {
        return chatCompletionContent.map((contentPart) => contentPart?.text ?? contentPart?.content ?? "").join("\n").trim();
    }

    const responseOutputText = responseBody?.output_text;

    if (typeof responseOutputText === "string") {
        return responseOutputText;
    }

    return null;
}

function getPurchaseOrderInvoiceAiApiErrorMessage(status: number, responseBodyText: string) {
    const parsedBody = safePurchaseOrderInvoiceAiJsonParse(responseBodyText);
    const apiMessage = parsedBody?.error?.message ?? parsedBody?.message;

    if (apiMessage) {
        return `AI request failed (${status}): ${apiMessage}`;
    }

    return `AI request failed (${status}).`;
}

function parsePurchaseOrderInvoiceAiJsonFromText(text: string) {
    const jsonText = extractPurchaseOrderInvoiceAiJsonObjectText(text);
    const parsed = safePurchaseOrderInvoiceAiJsonParse(jsonText);

    if (!parsed) {
        throw new Error("The AI response did not contain valid JSON.");
    }

    return parsed;
}

function safePurchaseOrderInvoiceAiJsonParse(text: string) {
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

function extractPurchaseOrderInvoiceAiJsonObjectText(text: string) {
    const trimmedText = String(text ?? '').trim();
    const fencedJsonMatch = trimmedText.match(/```(?:json)?\s*([\s\S]*?)```/i);

    if (fencedJsonMatch?.[1]) {
        return fencedJsonMatch[1].trim();
    }

    const firstBraceIndex = trimmedText.indexOf("{");
    const firstBracketIndex = trimmedText.indexOf("[");
    const startsWithArray = firstBracketIndex !== -1 && (firstBraceIndex === -1 || firstBracketIndex < firstBraceIndex);

    if (startsWithArray) {
        const lastBracketIndex = trimmedText.lastIndexOf("]");

        if (lastBracketIndex > firstBracketIndex) {
            return trimmedText.slice(firstBracketIndex, lastBracketIndex + 1);
        }
    }

    const lastBraceIndex = trimmedText.lastIndexOf("}");

    if (firstBraceIndex === -1 || lastBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) {
        return trimmedText;
    }

    return trimmedText.slice(firstBraceIndex, lastBraceIndex + 1);
}

function isPurchaseOrderInvoiceAiOpenAiApiUrl(url?: string | null) {
    return /(^https?:\/\/)?api\.openai\.com\//i.test(url ?? "");
}

function normalizePurchaseOrderInvoiceFileName(fileName: string) {
    return String(fileName ?? '').trim().toLowerCase();
}

function formatPurchaseOrderInvoiceChargeSum(sum: number | string) {
    if (typeof sum === 'number') {
        return Number.isInteger(sum) ? String(sum) : sum.toFixed(2).replace(/\.?0+$/, '');
    }

    return String(sum);
}

function formatPurchaseOrderInvoiceAiBytes(bytes: number) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return "0 B";
    }

    const units = ["B", "KB", "MB", "GB"];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, unitIndex);

    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

async function writePurchaseOrderInvoiceAiTextToClipboard(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    if (typeof document === "undefined") {
        throw new Error("Clipboard write is not available.");
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        const didCopy = document.execCommand("copy");
        if (!didCopy) {
            throw new Error("Clipboard copy command failed.");
        }
    } finally {
        document.body.removeChild(textArea);
    }
}


/* ========================= SUBMIT HOOKS & HELPERS ========================= */

export const useSubmitPurchaseOrder = (): [
    any,
    (
        values: any,
        changedRows: any[],
        formikHelpers: FormikHelpers<any>,
        options?: {
            nextStatusId?: number;
            guards?: { formDirty: boolean; rowsChanged: boolean };
            baselineRows?: any[];
        },
    ) => any,
] => {
    const combinedRequests = ({
        values,
        changedRows,
        nextStatusId,
        guards,
        baselineRows,
    }: {
        values: any;
        changedRows: any[];
        nextStatusId?: number;
        guards?: { formDirty: boolean; rowsChanged: boolean };
        baselineRows?: any[];
    }): AxiosRequestConfig[] => {
        const orderId = values?.PurchaseOrderId;
        const isCreate = orderId == null || orderId === 0;

        console.log('Changed Rows for Submit:', changedRows);
        console.log('Baseline Rows:', baselineRows);
        console.log('PurchaseOrderId:', orderId);

        const ALWAYS_STATUS_ONLY = new Set<number>([STATUS_ID['Received Short'], STATUS_ID.Cancelled]);

        if (!isCreate && nextStatusId) {
            if (ALWAYS_STATUS_ONLY.has(nextStatusId)) {
                return [buildStatusOnlyRequest(orderId, nextStatusId)];
            }
            if (!guards?.formDirty && !guards?.rowsChanged) {
                return [buildStatusOnlyRequest(orderId, nextStatusId)];
            }
        }

        // If we don't have an order id yet, fall back to "main create" only.
        // (You can revisit create flow later; row endpoints need an existing PO id.)
        if (isCreate) {
            const mainReq = buildPurchaseOrderRequest({ values, orderRows: changedRows, PurchaseOrderId: orderId });
            const mainRequests = Array.isArray(mainReq) ? mainReq : [mainReq];
            console.log('Create detected, mainRequests:', mainRequests);
            return mainRequests;
        }

        const nextRows = Array.isArray(changedRows) ? changedRows : [];
        const baseRows = Array.isArray(baselineRows) ? baselineRows : (values?.PurchaseOrderRows || []);

        const baseById = new Map<number, any>();
        baseRows.forEach((r: any) => {
            const id = Number(r?.PurchaseOrderRowId);
            if (Number.isFinite(id) && id > 0) baseById.set(id, r);
        });

        const isSameDateish = (a: any, b: any) => {
            if (!a && !b) return true;
            const da = a ? new Date(a).toISOString().slice(0, 10) : '';
            const db = b ? new Date(b).toISOString().slice(0, 10) : '';
            return da === db;
        };

        // Decide if a row should be updated (keep this focused on fields you actually edit)
        const rowChanged = (prev: any, cur: any) => {
            if (!prev) return true;
            const n = (v: any) => (v == null || v === '' ? null : Number(v));

            const prevQty = n(prev?.Quantity);
            const curQty = n(cur?.Quantity);

            const prevPrice = n(prev?.ItemPriceOrderCurrency ?? prev?.NegotiatedPrice ?? prev?.Price ?? prev?.ListPriceDefaultCurrency);
            const curPrice = n(cur?.ItemPriceOrderCurrency ?? cur?.NegotiatedPrice ?? cur?.Price ?? cur?.ListPriceDefaultCurrency);

            const prevDiscAmt = n(prev?.DiscountAmount);
            const curDiscAmt = n(cur?.DiscountAmount);

            const prevComment = String(prev?.Comment ?? '');
            const curComment = String(cur?.Comment ?? '');

            if (prevQty !== curQty) return true;
            if (prevPrice !== curPrice) return true;
            if (prevDiscAmt !== curDiscAmt) return true;
            if (prevComment !== curComment) return true;

            if (!isSameDateish(prev?.ETA, cur?.ETA)) return true;
            if (!isSameDateish(prev?.RTA, cur?.RTA)) return true;

            return false;
        };

        // --- UPDATE (only rows that changed vs baseline) ---
        const existingRows = nextRows.filter((r) => Number(r?.PurchaseOrderRowId ?? 0) > 0);
        const updateCandidates = existingRows.filter((r) => {
            const id = Number(r?.PurchaseOrderRowId);
            return rowChanged(baseById.get(id), r);
        });
        const updateRows = updateCandidates.map(mapRowForUpsert);

        // --- CREATE (new rows only) ---
        const newRowsRaw = nextRows.filter((r) => Number(r?.PurchaseOrderRowId ?? 0) <= 0);
        const createRows = newRowsRaw.map(mapRowToCreatePayload).filter((r) => {
            const ok =
                !!r?.SupplierAssortmentItemId &&
                typeof r.Quantity === 'number' &&
                typeof r.ItemPriceOrderCurrency === 'number';
            if (!ok) console.warn('Row dropped by create filter:', r);
            return ok;
        });

        // --- DELETE (removed rows only, based on baseline snapshot) ---
        const originalIds = new Set<number>(
            baseRows
                .map((r: any) => Number(r?.PurchaseOrderRowId))
                .filter((id) => Number.isFinite(id) && id > 0)
        );

        const keptIds = new Set<number>(
            existingRows
                .map((r: any) => Number(r?.PurchaseOrderRowId))
                .filter((id) => Number.isFinite(id) && id > 0)
        );

        const removedIds = Array.from(originalIds).filter((id) => !keptIds.has(id));

        const requests: AxiosRequestConfig[] = [];

        // Ordering experiment: try updates first, then deletes, then creates
        // (You can flip create last/first depending on lock behavior.)
        if (updateRows.length) requests.push(updatePurchaseOrderRowsById(orderId, updateRows));
        removedIds.forEach((id) => requests.push(deletePurchaseOrderRowById(orderId, id)));
        if (createRows.length) requests.push(createPurchaseOrderRowsById(orderId, createRows));

        // Always include main PO PATCH last (meta/status etc)
        const mainReq = buildPurchaseOrderRequest({ values, orderRows: nextRows, PurchaseOrderId: orderId });
        const mainRequests = Array.isArray(mainReq) ? mainReq : [mainReq];
        requests.push(...mainRequests);

        if (nextStatusId && orderId) requests.push(buildStatusOnlyRequest(orderId, nextStatusId));

        console.log('Combined Requests (update+delete+create+main):', requests);
        return requests;
    };

    const [resultMain, submitMain] = useSubmit<any>(combinedRequests, {
        logLevel: ['error', 'success', 'request'],
        sequential: true,
    });

    const submit = (values: any, changedRows: any[], formikHelpers: FormikHelpers<any>, options?: any) => {
        submitMain(
            {
                values,
                changedRows,
                nextStatusId: options?.nextStatusId,
                guards: options?.guards,
                baselineRows: options?.baselineRows,
            },
            formikHelpers,
        );
    };

    return [resultMain, submit];
};


const DEFAULT_DISCOUNT_TYPE = 3; // Amount

export const purchaseOrderRowsPostRequests = ({
    values,
    changedRows,
}: {
    values: any
    changedRows: any[]
}): AxiosRequestConfig[] => {
    const orderId = values?.PurchaseOrderId
    const originalRows: any[] = values?.PurchaseOrderRows || []

    const existingChanged = (changedRows || []).filter(r => (r?.PurchaseOrderRowId ?? 0) > 0)
    const newRowsRaw = (changedRows || []).filter(r => (r?.PurchaseOrderRowId ?? 0) <= 0)

    const updateRows = existingChanged.map(mapRowForUpsert)
    const createRows = newRowsRaw.map(mapRowToCreatePayload)

    const originalIds = new Set(originalRows.map(r => r.PurchaseOrderRowId).filter(id => id > 0))
    const changedIds = new Set(existingChanged.map(r => r.PurchaseOrderRowId).filter(id => id > 0))
    const removedIds = [...originalIds].filter(id => !changedIds.has(id))
    const deleteRows = removedIds.map(id => ({ PurchaseOrderRowId: id }))

    const requests: AxiosRequestConfig[] = []
    if (updateRows.length) requests.push(updatePurchaseOrderRowsById(orderId, updateRows))
    if (createRows.length) requests.push(createPurchaseOrderRowsById(orderId, createRows))
    removedIds.forEach(id => requests.push(deletePurchaseOrderRowById(orderId, id)))
    return requests
}

const mapRowForUpsert = (row: any) => {
    const isExisting = (row?.PurchaseOrderRowId ?? 0) > 0;
    console.log("Mapping row for upsert:", row, "Is existing:", isExisting);
    // Price/qty/ids normalization like your create mapper
    const normalizeNumber = (v: any) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
    const tryParseDate = (d?: string | null) => {
        if (!d) return undefined;
        const dt = new Date(d);
        return isNaN(dt.getTime()) ? undefined : dt.toISOString();
    };

    // Product identity: server supports ProductId string in your examples
    const productId =
        //row?.Product?.Key ??
        //row?.ProductId ??
        //row?.SupplierProduct?.Key ??
        (row?.SupplierAssortmentItemId != null ? String(row?.SupplierAssortmentItemId) : undefined);

    const qty = normalizeNumber(row?.Quantity) ?? 1;
    const price =
        normalizeNumber(row?.ItemPriceOrderCurrency) ??
        normalizeNumber(row?.NegotiatedPrice) ??
        normalizeNumber(row?.Price) ??
        normalizeNumber(row?.ListPriceOrderCurrency) ??
        normalizeNumber(row?.ListPriceDefaultCurrency);

    const eta = tryParseDate(row?.ETA);
    const rta = tryParseDate(row?.RTA);

    const discountRate = normalizeNumber(row?.DiscountRate);
    const discountAmount = normalizeNumber(row?.DiscountAmount);
    const disountType = row?.DisountType ?? row?.DiscountType; // keep server spelling if present

    const base: any = {
        // include id only for existing rows
        ...(isExisting ? { PurchaseOrderRowId: row.PurchaseOrderRowId } : {}),
        SupplierAssortmentItemId: productId != null ? String(productId) : undefined,
        Quantity: qty,
        ItemPriceOrderCurrency: price,
        ETA: eta,
        RTA: rta,
        Comment: row?.Comment || undefined,
        //DiscountRate: discountRate,
        DiscountAmount: discountAmount,
        DiscountType: DEFAULT_DISCOUNT_TYPE,
    };

    if (typeof disountType !== "undefined") base.DisountType = disountType;

    // Remove undefineds
    Object.keys(base).forEach((k) => base[k] === undefined && delete base[k]);

    return base;
};

export const buildPurchaseOrderRequest = (
    payload: { values: any; orderRows: any[], PurchaseOrderId?: any }
): AxiosRequestConfig => {
    return buildSubmitRequest({
        values: payload,
        createOrUpdateFn: createOrUpdatePurchaseOrder,
        mapCreate: toPOPayload,
        mapUpdate: toPOPayloadUpdate,
        idField: "PurchaseOrderId",
        updateMethod: "PATCH",
    })!;
};

const intOrUndefined = (v: any) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
};



const tryParseDate = (d?: string | null) => {
    if (!d) return undefined;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

const numOrUndef = (n: any) =>
    typeof n === "number" && Number.isFinite(n) ? n : undefined;

const mapRowToCreatePayload = (row: any) => {
    console.log("Mapping row to create payload:", row);
    const productId = (row?.SupplierAssortmentItemId != null ? String(row?.SupplierAssortmentItemId) : undefined);
    const itemPrice =
        numOrUndef(row?.ItemPriceOrderCurrency) ??
        numOrUndef(row?.NegotiatedPrice) ??
        numOrUndef(row?.Price) ??
        numOrUndef(row?.ListPriceOrderCurrency) ??
        numOrUndef(row?.ListPriceDefaultCurrency);

    const qty = numOrUndef(row?.Quantity) ?? 1;

    const eta = tryParseDate(row?.ETA);
    const rta = tryParseDate(row?.RTA);

    const discountRate = numOrUndef(row?.DiscountRate);
    const discountAmount = numOrUndef(row?.DiscountAmount);

    const disountType = row?.DisountType ?? row?.DiscountType;

    const out: any = {
        SupplierAssortmentItemId: productId != null ? String(productId) : undefined,
        ItemPriceOrderCurrency: itemPrice,
        Quantity: qty,
        ETA: eta,
        RTA: rta,
        Comment: row?.Comment || undefined,
        DiscountType: DEFAULT_DISCOUNT_TYPE,
        //DiscountRate: discountRate,
        DiscountAmount: discountAmount ?? 0,
    };

    if (typeof disountType !== "undefined") {
        out.DisountType = disountType; // keep API field spelling
    }

    // Strip undefined fields
    Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
    return out;
};

// ---------- Final payload ----------

export const toPOPayload = ({
    values,
    orderRows = [],
}: {
    values: any;
    orderRows?: any[];
}) => {

    const POData = {
        // --- Core PO fields ---
        PurchaseOrderType: 1, // number (matches your working example JSON)
        Recipient: intOrUndefined(values.Recipient),
        SupplierId: intOrUndefined(values.Supplier),
        SupplierContact: values.SupplierContact || undefined,
        Status: intOrUndefined(values.Status),

        // --- Currency ---
        DefaultCurrencyId: 1,
        OrderCurrency: {
            Currency: { Key: 4, Value: "EUR" },
            Description: "Euro",
            Enabled: true,
        },

        // --- Payment & shipping ---
        PaymentMethodId: intOrUndefined(values.PaymentMethodId),
        PaymentTermId: intOrUndefined(values.PaymentTermId),
        ShippingMethodId: intOrUndefined(values.ShippingMethod),
        ShippingTermId: intOrUndefined(values.ShippingTerm),
        ShippingTermsDestination: values.ShippingTermsDestination || undefined,
        SendDestination: values.SendDestination || undefined,
        SendType: intOrUndefined(values.SendType),

        Comment: values.Comment || undefined,


        PurchaseOrderRows: getMappedNewRows(orderRows),
    };
    return POData;
};

export const toPOPayloadUpdate = ({
    values,
}: {
    values: any;
    orderRows?: any[];
}) => {
    return {
        Status: intOrUndefined(values.Status),
        Comment: values.Comment || undefined,
        PaymentMethodId: intOrUndefined(values.PaymentMethodId),
        PaymentTermId: intOrUndefined(values.PaymentTermId),
        ShippingMethodId: intOrUndefined(values.ShippingMethod),
        ShippingTermId: intOrUndefined(values.ShippingTerm),
        ShippingTermsDestination: values.ShippingTermsDestination || undefined,
        SendDestination: values.SendDestination || undefined,
        SupplierInvoiceNo: values.SupplierInvoiceNo || undefined,
        CurrencyExchangeRate: numOrUndef(values.CurrencyExchangeRate),
    };
};

const getMappedNewRows = (orderRows: any[] = []) =>
    (orderRows || [])
        .filter(r => (r?.PurchaseOrderRowId ?? -1) <= 0)
        .map(mapRowToCreatePayload)
        .filter(r =>
            !!r.SupplierAssortmentItemId &&
            typeof r.Quantity === "number" &&
            typeof r.ItemPriceOrderCurrency === "number"
        );
/* -------------------- Minimal status-only PATCH request ------------------- */

const buildStatusOnlyRequest = (purchaseOrderId: number, statusId: number): AxiosRequestConfig => {
    // Reuse your submit builder to construct a minimal PATCH with only Status
    return buildSubmitRequest({
        values: {
            values: { PurchaseOrderId: purchaseOrderId },
            orderRows: [],
            PurchaseOrderId: purchaseOrderId
        },
        createOrUpdateFn: createOrUpdatePurchaseOrder,
        mapCreate: () => ({ Status: statusId }), // not used for create here
        mapUpdate: () => ({ Status: statusId }),
        idField: "PurchaseOrderId",
        updateMethod: "PATCH",
    })!;
};
